/* Deterministic synthetic warehouse-ops data.

   The warehouse-ops backend tables are empty, so the cockpit panel renders
   fabricated-but-stable data. Everything here is generated from a fixed seed
   via a small LCG/hash, so values never change across re-renders (no
   Math.random in render) yet still look rich and uneven like real telemetry. */

export type CoverState = "healthy" | "low" | "critical"
export type SlaStatus = "ok" | "at-risk" | "breached"
export type InboundStatus = "on-time" | "late" | "early"

export interface SkuRow {
  sku: string
  name: string
  category: string
  onHand: number
  /** Days-of-cover for each of the next N days (index 0 = today). */
  cover: number[]
  /** Convenience: today's days-of-cover (cover[0]). */
  coverNow: number
  /** Avg daily unit demand used to derive cover. */
  dailyDemand: number
}

export interface DcRow {
  code: string
  name: string
  region: string
  onTimePct: number
  /** Open order lines being fulfilled right now. */
  openLines: number
  /** Backlog of late lines. */
  backlog: number
  status: SlaStatus
  /** 7-point on-time% trend (oldest → newest) for a mini sparkline feel. */
  trend7: number[]
}

export interface InboundPo {
  po: string
  sku: string
  supplier: string
  /** Days from today the shipment is expected (can be 0 = today). */
  etaInDays: number
  qty: number
  status: InboundStatus
  /** Schedule slip in days (+late / -early / 0 on-time). */
  slipDays: number
}

export interface WarehouseData {
  generatedAt: string
  days: string[]
  skus: SkuRow[]
  dcs: DcRow[]
  inbound: InboundPo[]
  /** Rollup metrics for the header strip. */
  summary: {
    skuCount: number
    criticalCount: number
    lowCount: number
    avgOnTime: number
    breachedDcs: number
    inboundUnits: number
  }
}

/* ----- seeded pseudo-randomness (mulberry32) ----------------------------- */

function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Deterministic hash of a string → 32-bit int (so per-SKU streams differ). */
function hashStr(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function round(n: number, dp = 0): number {
  const f = 10 ** dp
  return Math.round(n * f) / f
}

export function coverState(days: number): CoverState {
  if (days < 3) return "critical"
  if (days < 10) return "low"
  return "healthy"
}

/* ----- catalog ----------------------------------------------------------- */

const CATALOG: ReadonlyArray<{ sku: string; name: string; category: string }> = [
  { sku: "SKU-1042", name: "Lithium Cell 21700", category: "Cells" },
  { sku: "SKU-2208", name: "BMS Controller v3", category: "Electronics" },
  { sku: "SKU-3317", name: "Aluminium Casing L", category: "Enclosure" },
  { sku: "SKU-4419", name: "Coolant Pump A2", category: "Thermal" },
  { sku: "SKU-5521", name: "Wiring Harness 48V", category: "Harness" },
  { sku: "SKU-6634", name: "Inverter Module Si", category: "Power" },
  { sku: "SKU-7740", name: "Sensor Array IR", category: "Sensing" },
  { sku: "SKU-8852", name: "Display Panel 7in", category: "HMI" },
  { sku: "SKU-9963", name: "Gasket Kit XL", category: "Seals" },
  { sku: "SKU-1075", name: "Fastener Pack M6", category: "Hardware" },
]

const DCS: ReadonlyArray<{ code: string; name: string; region: string }> = [
  { code: "DC-RTM", name: "Rotterdam Hub", region: "EU-West" },
  { code: "DC-SIN", name: "Singapore Cross-dock", region: "APAC" },
  { code: "DC-MEM", name: "Memphis FC", region: "NA-Central" },
  { code: "DC-DXB", name: "Dubai Logistics City", region: "MEA" },
]

const SUPPLIERS = ["Yantian Components", "Kobe Precision", "BASF Polymers", "Hanwha Cells", "TSMC Logistics", "Rhein Castings"]

/* ----- generator --------------------------------------------------------- */

const DAY_MS = 86_400_000

function dayLabel(base: Date, offset: number): string {
  const d = new Date(base.getTime() + offset * DAY_MS)
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

export function generateWarehouseData(seed = 42, dayCount = 14): WarehouseData {
  // Fixed base date keeps day labels stable across renders within a session.
  const base = new Date("2026-06-24T00:00:00Z")
  const days = Array.from({ length: dayCount }, (_, i) => dayLabel(base, i))

  const skus: SkuRow[] = CATALOG.map((c, idx) => {
    const rng = mulberry32(hashStr(c.sku) ^ seed)
    const dailyDemand = round(8 + rng() * 90)
    // Start with a believable on-hand and a per-SKU consumption/restock pattern.
    let onHand = Math.round(dailyDemand * (1.5 + rng() * 14))
    const startOnHand = onHand
    // Some SKUs are stressed (low starting cover) — bias a third toward risk.
    const stressed = idx % 3 === 0
    const cover: number[] = []
    let running = onHand
    for (let d = 0; d < dayCount; d++) {
      // Demand wobbles day to day; occasional restock event lifts cover.
      const demand = Math.max(1, dailyDemand * (0.6 + rng() * 0.9) * (stressed ? 1.25 : 1))
      const restock = rng() > (stressed ? 0.85 : 0.7) ? dailyDemand * (3 + rng() * 6) : 0
      running = Math.max(0, running - demand + restock)
      const coverDays = round(running / dailyDemand, 1)
      cover.push(coverDays)
    }
    onHand = startOnHand
    const coverNow = cover[0] ?? 0
    return {
      sku: c.sku,
      name: c.name,
      category: c.category,
      onHand,
      cover,
      coverNow,
      dailyDemand,
    }
  })

  const dcs: DcRow[] = DCS.map((dc) => {
    const rng = mulberry32(hashStr(dc.code) ^ seed ^ 0x9e3779b9)
    const onTimePct = round(78 + rng() * 21, 1) // 78–99
    const status: SlaStatus = onTimePct >= 95 ? "ok" : onTimePct >= 88 ? "at-risk" : "breached"
    const openLines = Math.round(120 + rng() * 880)
    const backlog = status === "breached" ? Math.round(20 + rng() * 140) : Math.round(rng() * 30)
    const trend7 = Array.from({ length: 7 }, (_, i) => {
      const drift = (i - 3) * (status === "breached" ? -0.9 : status === "at-risk" ? -0.3 : 0.4)
      return round(Math.min(100, Math.max(60, onTimePct + drift + (rng() - 0.5) * 4)), 1)
    })
    return { code: dc.code, name: dc.name, region: dc.region, onTimePct, openLines, backlog, status, trend7 }
  })

  const inboundRng = mulberry32(seed ^ 0xb5297a4d)
  const inbound: InboundPo[] = Array.from({ length: 7 }, (_, i) => {
    const cat = CATALOG[Math.floor(inboundRng() * CATALOG.length)] ?? CATALOG[0]!
    const supplier = SUPPLIERS[Math.floor(inboundRng() * SUPPLIERS.length)] ?? SUPPLIERS[0]!
    const etaInDays = i + Math.floor(inboundRng() * 2)
    const roll = inboundRng()
    const slipDays = roll > 0.72 ? 1 + Math.floor(inboundRng() * 4) : roll < 0.15 ? -(1 + Math.floor(inboundRng() * 2)) : 0
    const status: InboundStatus = slipDays > 0 ? "late" : slipDays < 0 ? "early" : "on-time"
    return {
      po: `PO-${48210 + i * 37}`,
      sku: cat.sku,
      supplier,
      etaInDays,
      qty: Math.round(200 + inboundRng() * 4800),
      status,
      slipDays,
    }
  }).sort((a, b) => a.etaInDays - b.etaInDays)

  const criticalCount = skus.filter((s) => coverState(s.coverNow) === "critical").length
  const lowCount = skus.filter((s) => coverState(s.coverNow) === "low").length
  const avgOnTime = round(dcs.reduce((a, d) => a + d.onTimePct, 0) / dcs.length, 1)
  const breachedDcs = dcs.filter((d) => d.status === "breached").length
  const inboundUnits = inbound.reduce((a, p) => a + p.qty, 0)

  return {
    generatedAt: base.toISOString(),
    days,
    skus,
    dcs,
    inbound,
    summary: { skuCount: skus.length, criticalCount, lowCount, avgOnTime, breachedDcs, inboundUnits },
  }
}
